import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useRoles } from '../hooks/useRoles';
import { useScopes } from '../hooks/useScopes';
import type { User } from 'oidc-client-ts';

/**
 * Context object passed to custom policy function
 */
export interface PolicyContext {
  /**
   * Current location from react-router
   */
  location: ReturnType<typeof useLocation>;

  /**
   * Any additional context you want to pass
   */
  [key: string]: any;
}

/**
 * Custom policy function for fine-grained access control
 */
export type PolicyFunction = (user: User, context: PolicyContext) => boolean;

export interface ProtectedRouteProps {
  /**
   * Child components to render when user is authorized
   */
  children: React.ReactNode;

  /**
   * Required realm roles (OR logic - user needs at least one)
   */
  requiredRoles?: string[];

  /**
   * Require all roles instead of any (AND logic)
   * @default false
   */
  requireAllRoles?: boolean;

  /**
   * Required OAuth 2.0 scopes (OR logic - user needs at least one)
   */
  requiredScopes?: string[];

  /**
   * Require all scopes instead of any (AND logic)
   * @default false
   */
  requireAllScopes?: boolean;

  /**
   * Custom policy function for advanced authorization logic
   * If provided, this takes precedence over role and scope checks
   */
  policy?: PolicyFunction;

  /**
   * Custom loading component to display while checking authentication
   */
  loadingComponent?: React.ReactNode;

  /**
   * Custom unauthorized component to display when user lacks required permissions
   */
  unauthorizedComponent?: React.ReactNode;

  /**
   * Path to redirect to if not authenticated
   * @default '/login'
   */
  redirectTo?: string;

  /**
   * Callback when user is unauthorized
   * @param reason - Reason for unauthorized access
   */
  onUnauthorized?: (
    reason: 'not-authenticated' | 'insufficient-roles' | 'insufficient-scopes' | 'policy-denied'
  ) => void;
}

/**
 * Component for protecting routes with authentication, role-based, scope-based, or policy-based authorization
 *
 * This component provides comprehensive route protection with multiple authorization strategies:
 * - Authentication check (always required)
 * - Role-based authorization (realm roles)
 * - Scope-based authorization (OAuth 2.0 scopes)
 * - Custom policy functions (advanced logic)
 *
 * @example Basic authentication required
 * ```tsx
 * import { ProtectedRoute } from 'antaeus.keycloak.react';
 *
 * <Route
 *   path="/dashboard"
 *   element={
 *     <ProtectedRoute>
 *       <Dashboard />
 *     </ProtectedRoute>
 *   }
 * />
 * ```
 *
 * @example Require specific role
 * ```tsx
 * <Route
 *   path="/admin"
 *   element={
 *     <ProtectedRoute requiredRoles={['admin']}>
 *       <AdminPanel />
 *     </ProtectedRoute>
 *   }
 * />
 * ```
 *
 * @example Require any of multiple roles
 * ```tsx
 * <ProtectedRoute requiredRoles={['editor', 'moderator', 'admin']}>
 *   <EditPage />
 * </ProtectedRoute>
 * ```
 *
 * @example Require all roles (AND logic)
 * ```tsx
 * <ProtectedRoute
 *   requiredRoles={['verified', 'premium']}
 *   requireAllRoles={true}
 * >
 *   <PremiumContent />
 * </ProtectedRoute>
 * ```
 *
 * @example Require specific scopes
 * ```tsx
 * <ProtectedRoute requiredScopes={['read:users', 'write:users']}>
 *   <UserManagement />
 * </ProtectedRoute>
 * ```
 *
 * @example Require all scopes (AND logic)
 * ```tsx
 * <ProtectedRoute
 *   requiredScopes={['read:data', 'write:data', 'delete:data']}
 *   requireAllScopes={true}
 * >
 *   <DataAdmin />
 * </ProtectedRoute>
 * ```
 *
 * @example Combine roles and scopes
 * ```tsx
 * <ProtectedRoute
 *   requiredRoles={['admin']}
 *   requiredScopes={['manage:users']}
 * >
 *   <UserAdmin />
 * </ProtectedRoute>
 * ```
 *
 * @example Custom policy function
 * ```tsx
 * <ProtectedRoute
 *   policy={(user, context) => {
 *     // Custom authorization logic
 *     const isEmailVerified = user.profile.email_verified === true;
 *     const isAdult = (user.profile.age || 0) >= 18;
 *     return isEmailVerified && isAdult;
 *   }}
 *   onUnauthorized={(reason) => {
 *     console.log('Access denied:', reason);
 *   }}
 * >
 *   <AdultContent />
 * </ProtectedRoute>
 * ```
 *
 * @example Advanced policy with location context
 * ```tsx
 * <ProtectedRoute
 *   policy={(user, context) => {
 *     // Check if user is accessing during business hours
 *     const hour = new Date().getHours();
 *     const isBusinessHours = hour >= 9 && hour < 17;
 *
 *     // Or check based on route
 *     const isAdminRoute = context.location.pathname.startsWith('/admin');
 *     const hasAdminRole = user.profile.realm_access?.roles?.includes('admin');
 *
 *     return isBusinessHours && (!isAdminRoute || hasAdminRole);
 *   }}
 * >
 *   <TimeSensitiveContent />
 * </ProtectedRoute>
 * ```
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRoles = [],
  requireAllRoles = false,
  requiredScopes = [],
  requireAllScopes = false,
  policy,
  loadingComponent,
  unauthorizedComponent,
  onUnauthorized,
}) => {
  const auth = useAuth();
  const { hasAnyRole, hasAllRoles } = useRoles();
  const { hasAnyScope, hasAllScopes } = useScopes();
  const location = useLocation();
  const hasRedirected = useRef(false);

  // Determine authorization state
  const isAuthenticated = !auth.isLoading && auth.isAuthenticated;

  // Check policy access
  const policyAccess = policy && auth.user
    ? policy(auth.user, { location })
    : null;

  // Check role access
  const roleAccess = requiredRoles.length > 0
    ? (requireAllRoles ? hasAllRoles(requiredRoles) : hasAnyRole(requiredRoles))
    : null;

  // Check scope access
  const scopeAccess = requiredScopes.length > 0
    ? (requireAllScopes ? hasAllScopes(requiredScopes) : hasAnyScope(requiredScopes))
    : null;

  // Determine unauthorized reason
  const unauthorizedReason: 'not-authenticated' | 'insufficient-roles' | 'insufficient-scopes' | 'policy-denied' | null =
    !isAuthenticated ? 'not-authenticated' :
    policyAccess === false ? 'policy-denied' :
    roleAccess === false ? 'insufficient-roles' :
    scopeAccess === false ? 'insufficient-scopes' :
    null;

  // All hooks must be called unconditionally at the top level
  // Handle unauthorized callback
  useEffect(() => {
    if (unauthorizedReason) {
      onUnauthorized?.(unauthorizedReason);
    }
  }, [unauthorizedReason, onUnauthorized]);

  // Handle login redirect for unauthenticated users
  useEffect(() => {
    if (!auth.isLoading && !auth.isAuthenticated && !hasRedirected.current) {
      hasRedirected.current = true;
      auth.signinRedirect({
        redirect_uri: window.location.origin + location.pathname + location.search,
      }).catch((error) => {
        // Reset flag on error to allow retry
        hasRedirected.current = false;
        console.error('[ProtectedRoute] Signin redirect failed:', error);
      });
    }
  }, [auth.isLoading, auth.isAuthenticated, location.pathname, location.search]);

  // Reset redirect flag when user becomes authenticated
  useEffect(() => {
    if (auth.isAuthenticated) {
      hasRedirected.current = false;
    }
  }, [auth.isAuthenticated]);

  // Show loading state while checking authentication
  if (auth.isLoading) {
    return <>{loadingComponent || <DefaultLoadingComponent />}</>;
  }

  // Check authentication
  if (!isAuthenticated) {
    return <>{loadingComponent || <DefaultLoadingComponent />}</>;
  }

  // Check authorization (policy takes precedence, then roles, then scopes)
  if (unauthorizedReason) {
    return <>{unauthorizedComponent || <DefaultUnauthorizedComponent />}</>;
  }

  // User is authenticated and has required permissions
  return <>{children}</>;
};

/**
 * Default loading component
 */
const DefaultLoadingComponent: React.FC = () => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '200px',
      flexDirection: 'column',
      gap: '1rem',
    }}
  >
    <div
      style={{
        border: '3px solid #f3f3f3',
        borderTop: '3px solid #3498db',
        borderRadius: '50%',
        width: '40px',
        height: '40px',
        animation: 'spin 1s linear infinite',
      }}
    />
    <p style={{ color: '#666', margin: 0 }}>Loading...</p>
    <style>{`
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `}</style>
  </div>
);

/**
 * Default unauthorized component
 */
const DefaultUnauthorizedComponent: React.FC = () => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '200px',
      flexDirection: 'column',
      gap: '1rem',
      padding: '2rem',
      textAlign: 'center',
    }}
  >
    <div
      style={{
        fontSize: '4rem',
        color: '#e74c3c',
      }}
    >
      🔒
    </div>
    <h1 style={{ margin: 0, color: '#333' }}>Access Denied</h1>
    <p style={{ color: '#666', margin: 0 }}>
      You do not have permission to access this page.
    </p>
  </div>
);
